import type { ComponentType, CSSProperties } from "react";
import {
  Banknote,
  FileSignature,
  CreditCard,
  Landmark,
  Phone,
  Globe,
  MinusCircle,
} from "lucide-react";
import type { TFunction } from "i18next";

import type { PaymentRail } from "@tilt/pos-sdk";

export type LucideIconComponent = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

/** Resolved icon: either a Lucide component (enum-backed) or a URL string (custom upload). */
export type ResolvedMethodIcon = LucideIconComponent | string;

export const ICON_KIND_MAP: Record<string, LucideIconComponent> = {
  banknote: Banknote,
  check: FileSignature,
  credit_card: CreditCard,
  bank: Landmark,
  phone: Phone,
  iframe: Globe,
  generic: MinusCircle,
};

export const COLOR_TOKEN_MAP: Record<string, { bg: string; text: string; ring: string }> = {
  emerald: { bg: "bg-emerald-500", text: "text-emerald-700", ring: "ring-emerald-300" },
  sky: { bg: "bg-sky-500", text: "text-sky-700", ring: "ring-sky-300" },
  violet: { bg: "bg-violet-500", text: "text-violet-700", ring: "ring-violet-300" },
  amber: { bg: "bg-amber-500", text: "text-amber-700", ring: "ring-amber-300" },
  neutral: { bg: "bg-gray-400", text: "text-gray-700", ring: "ring-gray-300" },
};

export function resolveMethodLabel(rail: PaymentRail, t: TFunction): string {
  if (rail.locale_key) {
    const translated = t(rail.locale_key, { defaultValue: "" });
    if (translated) return translated;
  }
  return rail.display_label_en;
}

/**
 * Returns a Lucide component for enum-backed icon_kind, or a URL string when
 * the super-admin has uploaded a custom icon.  Dark-mode variant is preferred
 * when `isDark` is true and an icon_dark_url is present.
 */
export function resolveMethodIcon(rail: PaymentRail, isDark = false): ResolvedMethodIcon {
  if (isDark && rail.icon_dark_url) return rail.icon_dark_url;
  if (rail.icon_light_url) return rail.icon_light_url;
  return ICON_KIND_MAP[rail.icon_kind] ?? ICON_KIND_MAP.generic;
}

export type MethodColors = {
  bg: string;
  text: string;
  ring: string;
  /** Inline style when a custom hex color is set; undefined for enum-backed colors. */
  bgStyle?: CSSProperties;
};

/**
 * Returns Tailwind class names for enum-backed color_token, or inline style
 * objects when the super-admin has set a custom hex accent color.
 *
 * When custom_color is set: bg/text/ring class names are empty strings and
 * bgStyle carries the CSS property to apply via the `style` prop.
 */
export function resolveMethodColors(rail: PaymentRail): MethodColors {
  if (rail.custom_color) {
    return {
      bg: "",
      text: "",
      ring: "",
      bgStyle: { backgroundColor: rail.custom_color },
    };
  }
  return COLOR_TOKEN_MAP[rail.color_token] ?? COLOR_TOKEN_MAP.neutral;
}

export function isMethodVoidable(rail: PaymentRail): boolean {
  return rail.is_voidable;
}

export function lookupMethodByTypeSubtype(
  rails: readonly PaymentRail[],
  paymentType: string,
  paymentSubtype: string | null,
): PaymentRail | null {
  return (
    rails.find(
      (r) =>
        r.payment_type === paymentType && (r.payment_subtype ?? null) === (paymentSubtype ?? null),
    ) ?? null
  );
}

export function lookupMethodByFeaturePath(
  rails: readonly PaymentRail[],
  featurePath: string,
): PaymentRail | null {
  return rails.find((r) => r.feature_path === featurePath) ?? null;
}
